import { useParams } from 'react-router-dom';
import { PdfViewer } from '../../components/PdfViewer';
import { PublicPageCloseButton } from '../../components/PublicPageCloseButton';
import { usePublicEstimate } from './hooks';
import { isExpired } from './isExpired';

export function PublicEstimateViewer() {
  const { nonce = '' } = useParams();
  const { data: estimate, isLoading, error } = usePublicEstimate(nonce);

  const expired = estimate ? isExpired(estimate.status, estimate.expiresAtUtc) : false;

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-center justify-between">
          <div className="text-sky-700 font-bold text-lg">ReceiptRaptor</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-slate-500">Secure estimate viewer</div>
            <PublicPageCloseButton />
          </div>
        </header>

        {isLoading && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">
            Loading estimate…
          </div>
        )}

        {error && <ErrorCard error={error} />}

        {estimate && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs uppercase text-slate-500 tracking-wide">From</p>
                <h1 className="text-xl font-semibold text-slate-800">{estimate.businessName}</h1>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase text-slate-500 tracking-wide">Estimate</p>
                <p className="font-mono">{estimate.estimateNumber}</p>
                <p className="text-xs text-slate-500">
                  {new Date(estimate.issuedAtUtc).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {expired ? (
                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                  Expired
                </span>
              ) : (
                <span className="inline-flex items-center text-xs px-2 py-0.5 rounded border bg-sky-50 text-sky-800 border-sky-200">
                  {estimate.status}
                </span>
              )}
              {estimate.expiresAtUtc && (
                <span className="text-xs text-slate-500">
                  Valid until {new Date(estimate.expiresAtUtc).toLocaleDateString()}
                </span>
              )}
            </div>

            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide">Prepared for:</p>
              <p className="text-slate-800">{estimate.customerName}</p>
            </div>

            <table className="w-full text-sm">
              <thead className="text-slate-600">
                <tr>
                  <th className="text-left font-medium pb-2">Description</th>
                  <th className="text-right font-medium pb-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {estimate.lineItems.map((li, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-2">{li.description}</td>
                    <td className="py-2 text-right">{li.lineTotal.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="text-right text-sm space-y-1 pt-2 border-t border-slate-100">
              <div>Subtotal: {estimate.subtotal.toFixed(2)} {estimate.currency}</div>
              {estimate.adjustments !== 0 && (
                <div>Adjustments: {estimate.adjustments.toFixed(2)} {estimate.currency}</div>
              )}
              <div className="text-lg font-semibold text-slate-800">
                Total: {estimate.total.toFixed(2)} {estimate.currency}
              </div>
            </div>

            <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-3">
              This is an estimate, not a bill. No payment is due.
            </p>

            {estimate.notes && (
              <div className="text-sm text-slate-600 border-t border-slate-100 pt-3 whitespace-pre-wrap">
                <strong className="text-slate-700">Notes: </strong>
                {estimate.notes}
              </div>
            )}

            {estimate.pdfUrl && (
              <div className="pt-2">
                <a
                  href={estimate.pdfUrl}
                  download={`${estimate.estimateNumber}.pdf`}
                  className="inline-flex bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 text-sm"
                >
                  Download PDF
                </a>
              </div>
            )}
          </section>
        )}

        {estimate?.pdfUrl && (
          <section className="bg-white border border-slate-200 rounded-xl p-3">
            <PdfViewer url={estimate.pdfUrl} />
          </section>
        )}

        <footer className="text-center text-xs text-slate-400 pt-4">
          Secured with a signed link. Valid for 7 days.
        </footer>
      </div>
    </div>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  let title = 'Something went wrong';
  let body = 'Please ask your sitter to resend the estimate.';

  if (status === 404) {
    title = 'Link not found';
    body = 'This estimate link is invalid or has been revoked.';
  } else if (status === 410) {
    title = 'Link expired';
    body = 'This estimate link has expired. Please ask your sitter to resend.';
  } else if (status === 429) {
    title = 'Too many requests';
    body = 'Please wait a minute and try again.';
  }

  return (
    <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      <p className="text-sm text-slate-600 mt-2">{body}</p>
    </div>
  );
}
